import {
  createColumnHelper,
  RowSelectionState,
  SortingState
} from "@tanstack/react-table";
import { TransactionRow } from "./types";
import { cx, formatToCurrency } from "shared/utils";
import { DateTime } from "luxon";
import { TruncateWithTooltip } from "shared/components";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Sorting01Icon,
  Sorting02Icon
} from "@hugeicons-pro/core-solid-rounded";
import { BankStatements } from "../../types";
import { isValidBankStatement } from "../../utils";

export const columnHelper = createColumnHelper<TransactionRow>();

export const getSharedColumns = (
  isDisabledFn: (row: TransactionRow) => boolean
) => [
  columnHelper.accessor((row) => row.data.transaction.description, {
    id: "name",
    header: "Name",
    size: 100,
    cell: ({ row }) => {
      const isDisabled = isDisabledFn(row.original);
      const isSelected = row.getIsSelected();
      const description = row.original.data.transaction.description || "-";
      return (
        <TruncateWithTooltip
          content={description}
          maxWidth="200px"
          tooltipClassName="z-[1000] border-white/40"
          className={cx(
            "text-sm",
            isDisabled ? "text-content-100" : "text-content-1000",
            isSelected && !isDisabled ? "font-bold" : ""
          )}
        />
      );
    }
  }),
  columnHelper.accessor((row) => row.data.transaction.date, {
    id: "date fixed_width",
    header: "Date",
    size: 110,
    cell: ({ row }) => {
      const date = row.original.data.transaction.date;
      return (
        <p className="text-sm text-content-1000 whitespace-nowrap">
          {date ? DateTime.fromISO(date).toFormat("MMM d, yyyy") : "-"}
        </p>
      );
    }
  }),
  columnHelper.accessor((row) => row.data.transaction.amount, {
    id: "amount right-align fixed_width",
    header: "Amount",
    size: 100,
    meta: {
      sortIcon: (desc?: boolean) => (
        <HugeiconsIcon
          icon={desc ? Sorting01Icon : Sorting02Icon}
          size={14}
          className="shrink-0"
        />
      )
    },
    cell: ({ row }) => {
      const { amount, currency_code } = row.original.data.transaction;
      const formattedValue = amount
        ? formatToCurrency({ cents: amount, currency: currency_code })
        : "-";

      return (
        <p className="text-sm font-bold text-right text-success-700">
          {formattedValue}
        </p>
      );
    }
  })
];

export const getAvailableData = (allBankStatements?: BankStatements) => {
  if (!allBankStatements)
    return { filteredBankStatements: [], transactions: [] };

  const filteredBankStatements =
    Object.values(allBankStatements).filter(isValidBankStatement);

  const rows: TransactionRow[] = filteredBankStatements.flatMap(
    (bankStatement, idx) =>
      bankStatement.items.map((item) => ({
        ...item,
        bankStatementAssetName:
          bankStatement.asset?.asset?.filename || `Statement #${idx}`
      }))
  );

  return { filteredBankStatements, transactions: rows };
};

export const isAssignedToOtherIncome = (
  row: TransactionRow,
  incomeId?: number
) => Boolean(row.income_id && row.income_id !== incomeId);

const searchByTransactionDescription = (
  transactions: TransactionRow[],
  search: string
) => {
  if (!search.trim()) return transactions;

  const lowerSearch = search.toLowerCase();
  return transactions.filter((tx) =>
    tx.data.transaction.description.toLowerCase().includes(lowerSearch)
  );
};

export const sortTransactions = (
  transactions: TransactionRow[],
  sortingState: SortingState
) => {
  const amountColSort = sortingState.find(({ id }) => id === "amount");

  return [...transactions].sort((a, b) => {
    if (!amountColSort) return 0;

    const diff = a.data.transaction.amount - b.data.transaction.amount;
    return amountColSort.desc ? -diff : diff;
  });
};

export const filterAndSortTransactions = ({
  transactions,
  search,
  sortingState
}: {
  transactions: TransactionRow[];
  search: string;
  sortingState: SortingState;
}) => {
  const searchResults = searchByTransactionDescription(transactions, search);
  return sortTransactions(searchResults, sortingState);
};

export const checkIfSelectionsDiffer = (
  transactions: TransactionRow[],
  initialSelection: RowSelectionState,
  currentSelection: RowSelectionState,
  incomeId?: number
) => {
  const currentIds = new Set(
    transactions
      .filter(
        (tx) =>
          currentSelection[String(tx.id)] &&
          !isAssignedToOtherIncome(tx, incomeId)
      )
      .map((tx) => tx.id)
  );
  const initialIds = new Set(
    transactions
      .filter(
        (tx) =>
          initialSelection[String(tx.id)] &&
          !isAssignedToOtherIncome(tx, incomeId)
      )
      .map((tx) => tx.id)
  );

  if (currentIds.size !== initialIds.size) return true;
  return !Array.from(currentIds).every((id) => initialIds.has(id));
};
